import get from "lodash/get";
import React from "react";
import { Button, Modal, Pagination, PlusIcon } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { useTranslation } from "react-i18next";

import ViolationTypesForm from "~/pages/Editor/EditorViolation/modals/ViolationTypesForm";
import Wrapper from "~/shared/ui/Wrapper";
import {
  useGetLocationData,
  useGoToPreviousPage,
  useSetLocationData,
} from "~/hooks/locationData";
import { getFormatDate } from "~/libs/getFormatDate";
import { EDITOR_VIOLATION_OPTIONS } from "~/pages/Editor/EditorViolation/options";
import {
  OrderByColumnInViolationTypes,
  SortType,
  useGetViolationTypesQuery,
  ViolationStatusType,
  ViolationTypeCategory,
} from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { TableV2 } from "~/shared/ui/TableV2";
import { ViolationFilter } from "~/widgets/Filter";

import { getEditorViolationColumns } from "./model/tableAdapter";

import s from "./EditorViolation.less";

const formId = "ViolationTypesForm";

const EditorViolation = () => {
  const { hasPermission, loading: loadingPermissions } = usePermissions();
  const { t } = useTranslation();
  const goBack = useGoToPreviousPage();
  const setDataReplace = useSetLocationData(true);
  const {
    page,
    count,
    searchText,
    sortField,
    sortType,
    openModal,
    fullname,
    event,
    searchTextForSelect,
    currentId,
  } = useGetLocationData();

  const openAddModal = () => {
    setDataReplace("openModal", "Add");
  };

  const { handleCount, handlePage } = usePaginationParams();

  const closeModal = () => {
    setDataReplace("openModal", "", "currentId", "");
  };

  const newStr = (str) => str[0].toUpperCase() + str.slice(1);

  const { data, loading, refetch } = useGetViolationTypesQuery({
    variables: {
      name: searchText ? searchText : undefined,
      status: fullname ? ViolationStatusType[fullname] : undefined,
      category: searchTextForSelect
        ? ViolationTypeCategory[searchTextForSelect]
        : undefined,
      SubdivisionId: event ? event : undefined,
      order: sortType
        ? SortType[newStr(sortType.toLowerCase())]
        : SortType["Asc"],
      orderBy: sortField
        ? OrderByColumnInViolationTypes[newStr(sortField)]
        : OrderByColumnInViolationTypes["Id"],
      page,
      count: count || 25,
    },
    fetchPolicy: "no-cache",
  });

  const violations = get(data, "ViolationTypesGetter.get.items", []).map(
    (item) => ({
      ...item,
      statusTranslate: t(`violation.${item.status}`),
      introducedAt: getFormatDate(item.introducedAt, true),
      deletedAt: getFormatDate(item.deletedAt, true),
    }),
  );
  const violationsTotal =
    Number(get(data, "ViolationTypesGetter.get.total", 0)) || 0;

  const violation = violations.find((i) => i.id === currentId);

  const columns = getEditorViolationColumns(t, EDITOR_VIOLATION_OPTIONS);

  const handleRowEdit = (record: (typeof violations)[number]) => {
    setDataReplace("openModal", "Edit", "currentId", record.id);
  };

  return (
    <Wrapper.Content
      title={t("violation.violation")}
      goBackHandler={goBack}
      txtBtn={t("ui.back")}
      filterComponent={<ViolationFilter />}
      controlComponent={
        <Flex gap={10}>
          {hasPermission("violationsTypes.create") && (
            <Button
              onClick={openAddModal}
              size={"middle"}
              icon={<PlusIcon size={16} />}
            >
              {t("ui.add")}
            </Button>
          )}
        </Flex>
      }
    >
      <TableV2
        wrapperClassName={s.tableWrapper}
        columns={columns}
        dataSource={violations}
        loading={loading}
        onRowEdit={
          hasPermission("violationsTypes.edit") ? handleRowEdit : undefined
        }
      />
      <Pagination
        count={count}
        page={page + 1}
        totalCount={violationsTotal}
        onChangeCount={handleCount}
        onChangePage={handlePage}
      />

      <Modal
        open={openModal === "Add" || openModal === "Edit"}
        title={
          openModal === "Add"
            ? t("violation.addViolation")
            : t("violation.editViolation")
        }
        onCancel={closeModal}
        size={"large"}
        closable
        centered
        destroyOnHidden
        okButton={{
          htmlType: "submit",
          form: formId,
          children: t("ui.save"),
        }}
      >
        <ViolationTypesForm
          refetch={refetch}
          setCloseModal={closeModal}
          violation={violation}
          hasPermission={hasPermission}
          formId={formId}
        />
      </Modal>
    </Wrapper.Content>
  );
};
export default EditorViolation;
